<lvr-heading>{{ 'contact.edit.title' | transloco }}</lvr-heading>

<form
	class="form-container"
	[formGroup]="form"
	lvrAutoSave="contact"
	[entityId]="draftId()"
	[autoLoad]="false"
	autocomplete="on"
>
	@let contactTypeCtrl = form.get('contactType');
	<section class="form-group">
		<!-- Contact Date -->
		@let contactedAtCtrl = form.get('contactedAt');
		<lvr-field [label]="'client.contact.date' | transloco | appendAsterisk">
			<mat-form-field
				lvrInput
				[lvrError]="
					hasError(
						contactedAtCtrl,
						'matDatepickerParse',
						'minDateExceeded',
						'maxDateExceeded',
						'matDatepickerMin',
						'matDatepickerMax',
						'required'
					)
				"
			>
				<input
					matInput
					[matDatepicker]="picker"
					formControlName="contactedAt"
					placeholder="09.10.2025"
					[max]="maxDate()"
					[min]="minDate()"
					[errorStateMatcher]="matcher"
				/>
				@if (hasError(contactedAtCtrl, 'matDatepickerParse')) {
					<mat-error>{{ 'base.form.error.date_format_invalid' | transloco: { format: dateFormatHint } }}</mat-error>
				} @else if (hasError(contactedAtCtrl, 'matDatepickerMin', 'minDateExceeded')) {
					<mat-error>{{
						'base.form.error.date_min_error'
							| transloco: { minDate: (dateRange()?.minStartDate | date: dateDisplayFormat) }
					}}</mat-error>
				} @else if (hasError(contactedAtCtrl, 'matDatepickerMax', 'maxDateExceeded')) {
					<mat-error>{{ 'base.form.error.date_max_error' | transloco }}</mat-error>
				} @else if (hasError(contactedAtCtrl, 'required')) {
					<mat-error>{{ 'base.form.error.contacted_at_required' | transloco }}</mat-error>
				} @else {
					<mat-hint>{{ dateFormatHint }}</mat-hint>
				}
				<mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
				<mat-datepicker #picker></mat-datepicker>
			</mat-form-field>
		</lvr-field>

		<!-- Keyword -->
		@let keywordCtrl = form.get('keyword');
		<lvr-field [label]="'base.field.keyword' | transloco | appendAsterisk">
			<mat-form-field lvrInput [lvrError]="hasError(keywordCtrl, 'required')">
				<input
					matInput
					formControlName="keyword"
					placeholder="Erstkontakt, Folgekontakt, Beratung, ..."
					[maxlength]="keywordCtrlMaxLength"
				/>
				@if (hasError(keywordCtrl, 'required')) {
					<mat-error>{{ 'base.form.error.keyword_required' | transloco }}</mat-error>
				} @else {
					<mat-hint>{{
						'base.form.hint.max_length'
							| transloco: { actual: keywordCtrl?.value?.length || 0, max: keywordCtrlMaxLength }
					}}</mat-hint>
				}
			</mat-form-field>
		</lvr-field>

		<!-- Confidential -->
		<mat-checkbox class="confidential-checkbox" formControlName="confidential">
			{{ 'base.field.confidential' | transloco }} {{ 'base.common.for' | transloco }} {{ user()?.firstName }}
			{{ user()?.lastName }}
		</mat-checkbox>

		<!-- Participants -->
		<lvr-participants
			formControlName="participants"
			[label]="'base.field.search_participants' | transloco"
			[clientId]="client().id"
			[clientFullName]="clientFullName()"
			[typeOfContact]="contactTypeCtrl?.value"
			[subject]="keywordCtrl?.value"
			[body]="form.controls.remark.value"
		></lvr-participants>

		<!-- Documents -->
		@let filesCtrl = form.get('documents');
		<lvr-document-upload
			[maxFileCount]="env.contactSettings.maxFiles"
			[hasError]="hasFileError(filesCtrl, 'requiredFile', 'maxFiles', 'maxFileSize', 'allowedFileTypes')"
			[multiple]="true"
			[disabled]="!canManageContactDocuments()"
			formControlName="documents"
			[showIcon]="true"
			[ownerId]="client().uuid"
			[maxFileSize]="env.fileSettings.maxFileSize"
			[subjectType]="subjectType"
		>
		</lvr-document-upload>
	</section>

	<section class="form-group">
		<!-- Contact Type -->
		<lvr-field [label]="'base.field.contact_type' | transloco | appendAsterisk">
			<lvr-searchable-select
				[formControl]="contactTypeSelectControl"
				[options]="contactTypes() ?? []"
				[optionValue]="contactTypeOptionValue"
				[optionLabel]="contactTypeOptionLabel"
				[placeholder]="'base.field.select_please' | transloco"
				[searchPlaceholder]="'base.field.search_placeholder' | transloco"
				[noResultsText]="'base.field.no_results' | transloco"
				[ariaLabel]="'base.field.contact_type' | transloco"
				[required]="true"
				[hasError]="hasError(contactTypeSelectControl, 'required')"
				[errorMessage]="'base.form.error.contact_type_required' | transloco"
			></lvr-searchable-select>
		</lvr-field>

		<!-- Remark -->
		<lvr-field [label]="'base.field.remark' | transloco">
			@let remarkCtrl = form.get('remark');
			<mat-form-field lvrInput [lvrError]="hasError(remarkCtrl, 'maxlength')">
				<textarea
					matInput
					formControlName="remark"
					placeholder="Eine kurze Notiz..."
					class="taller-textarea"
					[maxlength]="remarkCtrlMaxLength"
				></textarea>
				@if (hasError(remarkCtrl, 'maxlength')) {
					<mat-error>{{
						'base.form.hint.max_length'
							| transloco: { actual: remarkCtrl?.value?.length || 0, max: remarkCtrlMaxLength }
					}}</mat-error>
				} @else {
					<mat-hint>{{
						'base.form.hint.max_length'
							| transloco: { actual: remarkCtrl?.value?.length || 0, max: remarkCtrlMaxLength }
					}}</mat-hint>
				}
			</mat-form-field>
		</lvr-field>

		<!-- Aufgaben -->
		@if (isTasksPhase() && canReadTasks()) {
			<lvr-searchable-task-picker
				class="tasks"
				formControlName="selectedTasksIds"
				[phaseId]="phaseId()"
				[typeOfCareId]="typeOfCareId()"
				[searchPlaceholder]="'base.field.search_placeholder'"
				[noResultsText]="'base.field.no_results'"
				mode="edit"
			>
			</lvr-searchable-task-picker>
		}
	</section>

	<p lvrPadding [padHorizontal]="16" [padVertical]="0">{{ 'base.form.required' | transloco }}</p>

	<lvr-default-actions
		[disableSave]="form.invalid || !canEditContactsPermission() || isClientLookupPending()"
		[showLoading]="isClientLookupPending() || isLoading()"
		(save)="update()"
		(cancelSave)="cancel()"
	>
		<lvr-restore-actions entityType="contact" [entityId]="draftId()"></lvr-restore-actions>
	</lvr-default-actions>
</form>
